import { openFile } from "../utils/common";
import { emptySplitApi } from "./empty-split-api";
import {
  CreatedInOfficeFormSession,
  InOfficeFormSession,
  InOfficeFormSessionDelivery,
  IntakeForm,
  IntakeFormRequest,
  IntakeFormRequestInput,
  IntakeFormSubmissionPdfInput,
  OnboardingRequestResponse,
  RecordIntakeFormEdocsSaveBody,
  RecordIntakeFormEdocsSaveResponse,
  RecoverSubmissionsResponse
} from "./types/onboarding-request.type";

const onBoardingRequestsApi = emptySplitApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getIntakeForms: builder.query<
      IntakeForm[],
      {
        practice: number | undefined;
        clinic: number | undefined;
        is_active?: boolean;
        query?: string;
      }
    >({
      query: (params) => ({
        url: `/intake-forms/`,
        method: "GET",
        params
      }),
      providesTags: ["IntakeForms"]
    }),
    syncIntakeForms: builder.mutation<void, void>({
      query: () => ({
        url: `/intake-forms/sync/`,
        method: "POST"
      }),
      invalidatesTags: ["IntakeForms"]
    }),
    getAllIntakeForms: builder.query<IntakeForm[], void>({
      query: () => ({
        url: `/intake-forms/`,
        method: "GET"
      }),
      providesTags: ["IntakeForms"]
    }),
    updateIntakeForm: builder.mutation<
      IntakeForm,
      { id: number; is_active: boolean }
    >({
      query: ({ id, ...body }) => ({
        url: `/intake-forms/${id}/`,
        body,
        method: "PATCH"
      }),
      invalidatesTags: ["IntakeForms"]
    }),
    getIntakeFormRequests: builder.query<
      OnboardingRequestResponse,
      {
        patientId: string;
        ordering?: string;
        appointment?: string;
        /**
         * Optional RestQL projection. Omitted, the full payload comes back — 43KB and ~470ms for a
         * patient with 13 requests, because every submission carries its Jotform `json_data` and
         * `pretty_data`. A caller that needs a handful of fields should say so: the same list narrowed
         * to what the card-step gate reads is 1.9KB and ~90ms, with the same two SQL queries, so the
         * cost is serialization rather than the database.
         *
         * A distinct `query` is a distinct RTK Query cache entry, which is the point — a narrow
         * consumer must not evict the table's full payload.
         */
        query?: string;
      }
    >({
      query: ({ patientId, ordering = "-created_at", ...params }) => ({
        url: `/patients/${patientId}/intake-form-requests/`,
        method: "GET",
        params: { ordering, ...params }
      }),
      providesTags: ["OnboardingFormRequest"]
    }),
    downloadIntakeFormSubmissionPdf: builder.mutation<
      Blob,
      IntakeFormSubmissionPdfInput
    >({
      query(request) {
        return {
          url: `/patients/${request.patientId}/intake-form-requests/${request.requestId}/submissions/${request.submissionId}/pdf/`,
          method: "GET",
          headers: {
            Accept: "*/*"
          },
          responseHandler: async (response) => {
            const blob = await response.blob();

            if (!request.doNotSaveFile) {
              const fileUrl = window.URL.createObjectURL(blob);

              openFile(fileUrl);
            }

            return blob;
          },
          cache: "no-cache"
        };
      }
    }),
    createIntakeFormRequest: builder.mutation<
      IntakeFormRequest,
      IntakeFormRequestInput
    >({
      query: (intakeRequestData) => ({
        url: `/patients/${intakeRequestData.patient}/intake-form-requests/`,
        method: "POST",
        body: intakeRequestData
      }),
      invalidatesTags: ["OnboardingFormRequest"]
    }),
    sendReminderIntakeFormRequest: builder.mutation<
      void,
      {
        patientId: number;
        intakeId: number;
        notification_method?: number;
        notification_contact?: string;
      }
    >({
      query: ({ patientId, intakeId, ...body }) => ({
        url: `/patients/${patientId}/intake-form-requests/${intakeId}/reminder/`,
        method: "PATCH",
        body
      }),
      // The reminder is a new send: refetch so the row's Last Sent and Send History show it.
      invalidatesTags: ["OnboardingFormRequest"]
    }),
    recoverIntakeFormSubmissions: builder.mutation<
      RecoverSubmissionsResponse,
      { patientId: number; intakeId: number }
    >({
      query: ({ patientId, intakeId }) => ({
        url: `/patients/${patientId}/intake-form-requests/${intakeId}/recover-submissions/`,
        method: "POST",
        body: { limit: 100 }
      }),
      invalidatesTags: ["OnboardingFormRequest", "IntakeFormSubmissionsReport"]
    }),
    addFormsToIntakeFormRequest: builder.mutation<
      IntakeFormRequest,
      { patientId: number; intakeId: number; intakeFormIds: number[] }
    >({
      query: ({ patientId, intakeId, intakeFormIds }) => ({
        url: `/patients/${patientId}/intake-form-requests/${intakeId}/`,
        method: "PATCH",
        body: {
          intake_form_request_submissions: {
            create: intakeFormIds.map((intake_form) => ({ intake_form }))
          }
        }
      }),
      invalidatesTags: ["OnboardingFormRequest", "IntakeFormSubmissionsReport"]
    }),
    cancelIntakeFormRequest: builder.mutation<
      void,
      { patientId: number; intakeId: number; submissionIds?: number[] }
    >({
      query: ({ patientId, intakeId, submissionIds }) => ({
        url: `/patients/${patientId}/intake-form-requests/${intakeId}/cancel/`,
        method: "PATCH",
        // Omitted entirely when not narrowing — an older backend ignores an absent key but the
        // whole-request cancel path must stay byte-identical to before the feature.
        ...(submissionIds?.length
          ? { body: { submission_ids: submissionIds } }
          : {})
      }),
      invalidatesTags: [
        "IntakeForms",
        "OnboardingFormRequest",
        "IntakeFormSubmissionsReport"
      ]
    }),
    /**
     * Record that submitted forms were just filed in E-Docs (who, when, which Box file). The upload
     * itself goes through the shared e-documents endpoint, which writes nothing to the database;
     * this is what makes the "In E-Docs" chip and "Save again" possible. Answers with a receipt; the list is refetched
     * through the invalidated tag. Only called with `INTAKE_FORM_EDOCS_SAVE_TRACKING_FEATURE` on — an older
     * backend has no such endpoint.
     */
    recordIntakeFormEdocsSave: builder.mutation<
      RecordIntakeFormEdocsSaveResponse,
      {
        patientId: number;
        requestId: number;
        body: RecordIntakeFormEdocsSaveBody;
      }
    >({
      query: ({ patientId, requestId, body }) => ({
        url: `/patients/${patientId}/intake-form-requests/${requestId}/edocs-saves/`,
        method: "POST",
        body
      }),
      invalidatesTags: ["OnboardingFormRequest", "IntakeFormSubmissionsReport"]
    }),
    /**
     * In-office links for one request, newest first: who opened each, how, when the patient opened it
     * and finished. Polled while the "Fill in Office" dialog is open. Only called with
     * `IN_OFFICE_INTAKE_FORMS_FEATURE` on — an older backend has no such endpoint.
     */
    getInOfficeFormSessions: builder.query<
      InOfficeFormSession[],
      { patientId: number; requestId: number }
    >({
      query: ({ patientId, requestId }) => ({
        url: `/patients/${patientId}/intake-form-requests/${requestId}/in-office-sessions/`
      }),
      providesTags: (_result, _error, { requestId }) => [
        { type: "InOfficeFormSession", id: requestId }
      ]
    }),
    /**
     * Every in-office link for the patient's requests in ONE call: what the Onboardings tab's audit
     * bands read, so the tab does not make one request per request row.
     */
    getPatientInOfficeFormSessions: builder.query<
      InOfficeFormSession[],
      { patientId: number }
    >({
      query: ({ patientId }) => ({
        url: `/patients/${patientId}/intake-form-requests/in-office-sessions/`
      }),
      providesTags: (_result, _error, { patientId }) => [
        { type: "InOfficeFormSession", id: `patient-${patientId}` }
      ]
    }),
    createInOfficeFormSession: builder.mutation<
      CreatedInOfficeFormSession,
      {
        patientId: number;
        requestId: number;
        delivery: InOfficeFormSessionDelivery;
      }
    >({
      query: ({ patientId, requestId, delivery }) => ({
        url: `/patients/${patientId}/intake-form-requests/${requestId}/in-office-sessions/`,
        method: "POST",
        body: { delivery }
      }),
      invalidatesTags: (_result, _error, { patientId, requestId }) => [
        { type: "InOfficeFormSession", id: requestId },
        { type: "InOfficeFormSession", id: `patient-${patientId}` }
      ]
    }),
    revokeInOfficeFormSession: builder.mutation<
      InOfficeFormSession,
      { patientId: number; requestId: number; sessionId: number }
    >({
      query: ({ patientId, requestId, sessionId }) => ({
        url: `/patients/${patientId}/intake-form-requests/${requestId}/in-office-sessions/${sessionId}/revoke/`,
        method: "POST"
      }),
      invalidatesTags: (_result, _error, { patientId, requestId }) => [
        { type: "InOfficeFormSession", id: requestId },
        { type: "InOfficeFormSession", id: `patient-${patientId}` }
      ]
    })
  })
});

export const {
  useAddFormsToIntakeFormRequestMutation,
  useCreateInOfficeFormSessionMutation,
  useGetInOfficeFormSessionsQuery,
  useGetPatientInOfficeFormSessionsQuery,
  useRevokeInOfficeFormSessionMutation,
  useGetIntakeFormsQuery,
  useGetIntakeFormRequestsQuery,
  useCreateIntakeFormRequestMutation,
  useDownloadIntakeFormSubmissionPdfMutation,
  useSendReminderIntakeFormRequestMutation,
  useRecoverIntakeFormSubmissionsMutation,
  useCancelIntakeFormRequestMutation,
  useRecordIntakeFormEdocsSaveMutation,
  useUpdateIntakeFormMutation,
  useSyncIntakeFormsMutation,
  useGetAllIntakeFormsQuery
} = onBoardingRequestsApi;
